import styled from 'foundations-theming/utils';
import { Logo } from 'foundations-components/transitional/components/Logo';
import { SocialBlockXIcon } from 'foundations-assets/icons/components/SocialBlockXIcon';
import { SOCIAL_MAP } from '../../Constants';
import { Box } from 'foundations-components/transitional/components/Box';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Fraction of the avatar the badge scales to, so the mark stays proportional
// across avatar sizes.
const SOCIAL_BADGE_SCALE = 0.65;
// Fallback floor when a network declares no minimum of its own.
const DEFAULT_MIN_SOCIAL_BADGE_SIZE = 20;
// Gap between the avatar and the badge that sits beside it.
export const SOCIAL_BADGE_GAP = 8;

// Single source of truth for whether a social badge actually renders, narrowing
// `socialNetwork` to a real network when it does. The badge is hidden for
// responsive avatars and for networks without a logo, so callers (e.g. the
// layout wrapper that reserves space for it) must not assume a badge shows just
// because a socialNetwork was passed.
export const shouldShowSocialBadge = (socialNetwork, size) => {
  var _SOCIAL_MAP$socialNet;
  return size !== 'responsive' && Boolean(socialNetwork && ((_SOCIAL_MAP$socialNet = SOCIAL_MAP[socialNetwork]) === null || _SOCIAL_MAP$socialNet === void 0 ? void 0 : _SOCIAL_MAP$socialNet.logoName));
};

// Responsive badge size for a given (numeric) avatar size, floored at the
// network's minimum so it never renders too small (e.g. YouTube stays at least
// 28px). Callers resolve the numeric avatar size behind shouldShowSocialBadge,
// which already excludes the non-numeric responsive size.
export const getSocialBadgeSize = (socialNetwork, avatarSize) => {
  var _SOCIAL_MAP$socialNet2, _SOCIAL_MAP$socialNet3;
  const min = (_SOCIAL_MAP$socialNet2 = (_SOCIAL_MAP$socialNet3 = SOCIAL_MAP[socialNetwork]) === null || _SOCIAL_MAP$socialNet3 === void 0 ? void 0 : _SOCIAL_MAP$socialNet3.minSize) !== null && _SOCIAL_MAP$socialNet2 !== void 0 ? _SOCIAL_MAP$socialNet2 : DEFAULT_MIN_SOCIAL_BADGE_SIZE;
  return Math.max(Math.round(avatarSize * SOCIAL_BADGE_SCALE), min);
};
const SocialBadgeWrapper = styled.div.withConfig({
  displayName: "SocialBadge__SocialBadgeWrapper"
})(["", ""], ({
  theme,
  $showBackground,
  $size
}) => ({
  position: 'absolute',
  insetInlineEnd: `calc(-${$size}px - ${SOCIAL_BADGE_GAP}px)`,
  top: '50%',
  width: `${$size}px`,
  height: `${$size}px`,
  transform: 'translateY(-50%)',
  backgroundColor: $showBackground ? theme.color['fill-surface-default'] : undefined,
  borderRadius: $showBackground ? theme.borderRadius['400'] : undefined,
  lineHeight: '0'
}));
const XIconBadge = styled.div.withConfig({
  displayName: "SocialBadge__XIconBadge"
})(["", ""], ({
  $badgeSize
}) => ({
  fontSize: $badgeSize,
  position: 'relative'
}));
const SocialBadge = ({
  socialNetwork,
  size,
  badgeSize
}) => {
  if (!shouldShowSocialBadge(socialNetwork, size)) {
    return null;
  }
  const {
    logoName,
    showBackground
  } = SOCIAL_MAP[socialNetwork];
  const sizeValue = `${badgeSize}px`;
  const isXSocial = socialNetwork === 'x' || socialNetwork === 'twitter';
  return /*#__PURE__*/_jsx(SocialBadgeWrapper, {
    $showBackground: showBackground,
    $size: badgeSize,
    "data-test-id": "avatar-social-badge",
    "data-avatar-name": socialNetwork,
    children: isXSocial ? /*#__PURE__*/_jsxs(XIconBadge, {
      $badgeSize: sizeValue,
      children: [/*#__PURE__*/_jsx(Box, {
        position: "absolute",
        backgroundColor: "fill-surface-default",
        insetInlineStart: "10%",
        insetInlineEnd: "10%",
        insetBlockStart: "10%",
        insetBlockEnd: "10%"
      }), /*#__PURE__*/_jsx(Box, {
        position: "relative",
        children: /*#__PURE__*/_jsx(SocialBlockXIcon, {
          color: "do-not-use-x",
          size: "inherit"
        })
      })]
    }) : /*#__PURE__*/_jsx(Logo, {
      name: logoName,
      width: sizeValue,
      height: sizeValue
    })
  });
};
export default SocialBadge;